import React, { useState } from "react"

// L'esempio dell'assente e del sostituto: una lezione della scuola tennis
// con i suoi abbonati. Si tocca chi manca, gli spetta un recupero, e al suo
// posto si sceglie qualcuno: chi ha un recupero da usare lo consuma, chi non
// ne ha paga la volta. Le frasi sono quelle che l'app scrive davvero
// (AddSubstituteModal).

const PLAYERS = ["Tommaso Neri", "Sofia Riva", "Leo Conti", "Emma Sala"]
const POOL = [
	{ name: "Bianca Villa", from: "dalla scuola tennis del martedì" },
	{ name: "Nico Serra", from: "dalla scuola tennis del martedì" },
	{ name: "Alice Moro", pays: true },
]
const START = { absent: {}, subs: {}, picking: null, recoveries: { "Bianca Villa": 2, "Nico Serra": 1 }, message: null }

const firstName = name => name.split(" ")[0]
const hasLeft = n =>
	n === 1 ? "Ha 1 lezione da recuperare in questa stagione" : `Ha ${n} lezioni da recuperare in questa stagione`
const afterThis = n =>
	n - 1 === 0
		? "Con questo recupero non gliene restano altri"
		: n - 1 === 1
			? "Con questo recupero gliene resta 1 in questa stagione"
			: `Con questo recupero gliene restano ${n - 1} in questa stagione`

const SwapIcon = () => (
	<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<path d="M4 8h15M15 4l4 4-4 4M20 16H5M9 12l-4 4 4 4" />
	</svg>
)

function DemoSub() {
	const [state, setState] = useState(START)
	const { absent, subs, picking, recoveries, message } = state
	const used = Object.values(subs).map(s => s.name)

	const setSub = (who, sub) => {
		const nextRecoveries = { ...recoveries }
		const previous = subs[who]
		if (previous?.recovery) nextRecoveries[previous.name] += 1
		const nextSubs = { ...subs }
		let text
		if (sub) {
			nextSubs[who] = sub
			if (sub.recovery) {
				nextRecoveries[sub.name] -= 1
				text = `${sub.name} recupera qui al posto di ${firstName(who)}. ${afterThis(recoveries[sub.name])}.`
			} else {
				text = `${sub.name} viene al posto di ${firstName(who)} e paga la volta: 15 €, nei tuoi incassi del mese.`
			}
		} else {
			delete nextSubs[who]
			text = previous?.recovery
				? `Tolto il sostituto: il recupero torna a ${previous.name}.`
				: `Tolto il sostituto: la volta di ${previous?.name} non si conta più.`
		}
		setState({ ...state, subs: nextSubs, recoveries: nextRecoveries, picking: null, message: text })
	}

	const toggle = name => {
		const nextAbsent = { ...absent }
		if (absent[name]) {
			delete nextAbsent[name]
			const sub = subs[name]
			const nextSubs = { ...subs }
			const nextRecoveries = { ...recoveries }
			if (sub) {
				delete nextSubs[name]
				if (sub.recovery) nextRecoveries[sub.name] += 1
			}
			setState({
				...state,
				absent: nextAbsent,
				subs: nextSubs,
				recoveries: nextRecoveries,
				picking: picking === name ? null : picking,
				message: sub
					? `${firstName(name)} di nuovo presente: il sostituto non serve più.`
					: `${firstName(name)} di nuovo presente: niente recupero.`,
			})
		} else {
			nextAbsent[name] = true
			setState({
				...state,
				absent: nextAbsent,
				message: `${firstName(name)} è assente: gli spetta un recupero, da usare quando vuole entro la stagione. Al suo posto puoi far venire qualcuno.`,
			})
		}
	}

	const names = PLAYERS.map(name =>
		subs[name]
			? { name: subs[name].name, note: `al posto di ${name}`, isSub: true }
			: { name, note: absent[name] ? "assente" : "Abbonamento", isAbsent: Boolean(absent[name]) },
	)
	const candidates = POOL.filter(c => !used.includes(c.name)).map(c => {
		const left = recoveries[c.name] ?? 0
		const recovery = !c.pays && left > 0
		return { ...c, recovery, line: recovery ? hasLeft(left) : "Paga la volta · 15 €" }
	})
	const changed = Object.keys(absent).length > 0 || Object.keys(subs).length > 0

	return (
		<div className="demo demo-sub">
			<div className="demo-sub-card">
				<div className="demo-lesson-row">
					<span>16:30 - 17:30</span>
					<span className="demo-lesson-type">
						Scuola tennis ·
						<span className="demo-level" style={{ background: "var(--c-level-orange)" }} />
						Orange
					</span>
				</div>
				<div className="demo-sub-names">
					{names.map(n => (
						<div key={n.name}>
							{n.isSub ? (
								<span className="demo-sub-tag">
									<SwapIcon />
									<b>{n.name}</b>
								</span>
							) : (
								<b className={n.isAbsent ? "is-absent" : ""}>{n.name}</b>
							)}
							<small> · {n.note}</small>
						</div>
					))}
				</div>
				<div className="demo-lesson-pills">
					<span className="demo-court">
						Campo 1
						<span className="demo-court-dot" style={{ background: "var(--c-clay)" }} />
					</span>
					<span className="demo-pill">Tu, Chiara</span>
				</div>

				<p className="demo-sub-label">Presenze abbonati:</p>
				<div className="demo-presence">
					{PLAYERS.map((name, i) => {
						const isAbsent = Boolean(absent[name])
						const sub = subs[name]
						return (
							<div key={name} className="demo-presence-item">
								<button
									type="button"
									className="demo-presence-row"
									aria-pressed={!isAbsent}
									aria-label={`${name}, ${isAbsent ? "assente" : "presente"}`}
									onClick={() => toggle(name)}>
									<span>
										{name}
										{isAbsent && <small>1 lezione da recuperare entro la stagione</small>}
									</span>
									<span className={`demo-switch${isAbsent ? "" : " is-on"}`} aria-hidden="true">
										<span>{isAbsent ? "A" : "P"}</span>
									</span>
								</button>
								{sub && (
									<div className="demo-sub-chosen">
										<SwapIcon />
										<span>
											<b>{sub.name}</b>
											<small>Sostituto · {sub.recovery ? "recupero" : "contanti · 15 €"}</small>
										</span>
										<button type="button" aria-label={`Togli ${sub.name}`} onClick={() => setSub(name, null)}>
											✕
										</button>
									</div>
								)}
								{isAbsent && !sub && picking !== name && (
									<button
										type="button"
										className="demo-sub-add"
										onClick={() => setState({ ...state, picking: name })}>
										+ Aggiungi sostituto
									</button>
								)}
								{picking === name && (
									<div className="demo-sub-picker">
										<b>Chi viene al posto di {firstName(name)}?</b>
										{candidates.map(c => (
											<button
												type="button"
												key={c.name}
												onClick={() => setSub(name, { name: c.name, recovery: c.recovery })}>
												<span>
													{c.name}
													<small>{c.line}</small>
												</span>
												<span className={c.recovery ? "is-green" : "demo-muted"}>
													{c.recovery ? "Recupero" : "15 €"}
												</span>
											</button>
										))}
										<button
											type="button"
											className="demo-link"
											onClick={() => setState({ ...state, picking: null })}>
											Annulla
										</button>
									</div>
								)}
								{i < PLAYERS.length - 1 && <hr />}
							</div>
						)
					})}
				</div>

				<p className="demo-message" aria-live="polite">
					{message || "Tocca un allievo per segnarlo assente."}
				</p>
				{changed && (
					<button type="button" className="demo-link demo-reset" onClick={() => setState(START)}>
						Ricomincia
					</button>
				)}
			</div>
		</div>
	)
}

export default DemoSub
